import { useMemo } from 'react';
import { isReopenModifyHudCertsEnabled } from '../QualificationForms/utils';
import { HUD_PROGRAM_NAME } from './constants';
import { TAB_INDEX } from './CertificationStatusTabs/CertificationStatusTabs.constants';
import { EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS } from './stableEmptyDefaults';

export const IS_HUD_COMPLETED_TAB_PARAM = 'isHudCompletedTab';

export const CERT_TAB = {
  REOPENED: 'reopened',
  COMPLETED: 'completed',
};

export function isQualificationCurrentlyReopened(qualificationData) {
  return (
    qualificationData?.certificationStatus === 'REOPEN' &&
    qualificationData?.isActive === true
  );
}

export function getAffordableQualificationRowId(row) {
  const id =
    row?.id ?? row?.qualificationData?.id ?? row?.affordableQualificationId;

  if (id == null || id === '') {
    return null;
  }

  return String(id);
}

const COMPLETED_TAB_ROW_KEYS = new Set([
  'qualificationData',
  'assistanceDetails',
  'eventHouseholdMembers',
  'affordableQualificationCorrections',
  'exceptions',
  'backgroundAtAdmission',
  'familySelfSufficiency',
  'masterAffordableProgramId',
  'masterAffordableProgramName',
  'unitId',
  'certUnitNumber',
  'tenantEventData',
  'snapshotCreatedAt',
]);

function isFlatCompletedTabData(completedTabData) {
  if (!completedTabData || completedTabData.qualificationData) {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(
      completedTabData,
      'certificationStatus',
    ) ||
    Object.prototype.hasOwnProperty.call(completedTabData, 'hhAnnualIncome') ||
    Object.prototype.hasOwnProperty.call(completedTabData, 'isActive')
  );
}

export function normalizeCompletedTabData(completedTabData) {
  if (!completedTabData || !isFlatCompletedTabData(completedTabData)) {
    return completedTabData;
  }

  const rowFields = {};
  const qualificationData = {};

  Object.entries(completedTabData).forEach(([key, value]) => {
    if (COMPLETED_TAB_ROW_KEYS.has(key)) {
      rowFields[key] = value;
    } else {
      qualificationData[key] = value;
    }
  });

  return {
    ...rowFields,
    qualificationData,
  };
}

export function qualRowForTab(row, tab) {
  if (tab === CERT_TAB.COMPLETED && row?.completedTabData) {
    const completedTabData = normalizeCompletedTabData(row.completedTabData);
    const snapshotQual = completedTabData.qualificationData ?? {};
    const liveQual = row.qualificationData ?? {};
    const isLiveReopened = isQualificationCurrentlyReopened(liveQual);

    return {
      ...row,
      ...completedTabData,
      masterAffordableProgramName:
        completedTabData.masterAffordableProgramName ??
        row.masterAffordableProgramName,
      qualificationData: {
        ...snapshotQual,
        id: liveQual.id ?? snapshotQual.id,
        voucherEffectiveDate:
          snapshotQual.voucherEffectiveDate ?? liveQual.voucherEffectiveDate,
        certificationType:
          snapshotQual.certificationType ?? liveQual.certificationType,
        hudTenantEvent: liveQual.hudTenantEvent ?? snapshotQual.hudTenantEvent,
        ...(isLiveReopened
          ? {}
          : {
              isCorrection: liveQual.isCorrection ?? snapshotQual.isCorrection,
              isActive: liveQual.isActive ?? snapshotQual.isActive,
              certificationStatus:
                liveQual.certificationStatus ??
                snapshotQual.certificationStatus,
            }),
      },
    };
  }
  return row;
}

export function shouldUseHudCompletedTabFormReads({
  flags = {},
  programName = '',
  certTab = null,
} = {}) {
  if (!isReopenModifyHudCertsEnabled(flags)) {
    return false;
  }
  if (programName !== HUD_PROGRAM_NAME) {
    return false;
  }
  return certTab != null && `${certTab}` === `${TAB_INDEX.COMPLETED}`;
}

export function shouldFetchHudCompletedTabSnapshot({
  flags = {},
  programName = '',
  tabIndex,
  affordableQualificationId,
  affordableQualifications = [],
} = {}) {
  if (!isReopenModifyHudCertsEnabled(flags)) {
    return false;
  }
  if (programName !== HUD_PROGRAM_NAME) {
    return false;
  }
  if (tabIndex !== TAB_INDEX.COMPLETED) {
    return false;
  }
  if (!affordableQualificationId) {
    return false;
  }

  const targetId = String(affordableQualificationId);
  const row = affordableQualifications.find(
    (q) => getAffordableQualificationRowId(q) === targetId,
  );

  return Boolean(row?.completedTabData);
}

export function buildHudCompletedTabFetchParams({
  flags = {},
  programName = '',
  tabIndex,
  affordableQualificationId,
  affordableQualifications = [],
} = {}) {
  const isHudCompletedTab = shouldFetchHudCompletedTabSnapshot({
    flags,
    programName,
    tabIndex,
    affordableQualificationId,
    affordableQualifications,
  });

  return {
    isHudCompletedTab,
    affordableQualificationId: affordableQualificationId ?? null,
  };
}

export function useHudCompletedTabFetchParams({
  flags = {},
  programName = '',
  tabIndex,
  affordableQualificationId,
  affordableQualifications = [],
} = {}) {
  return useMemo(
    () =>
      buildHudCompletedTabFetchParams({
        flags,
        programName,
        tabIndex,
        affordableQualificationId,
        affordableQualifications,
      }),
    [
      affordableQualificationId,
      affordableQualifications,
      flags,
      programName,
      tabIndex,
    ],
  );
}

export function appendIsHudCompletedTabQuery(
  url,
  { enabled = false, affordableQualificationId } = {},
) {
  if (!enabled) {
    return url;
  }

  const params = new URLSearchParams();
  params.set(IS_HUD_COMPLETED_TAB_PARAM, 'true');

  if (affordableQualificationId) {
    params.set('affordableQualificationId', String(affordableQualificationId));
  }

  const queryString = params.toString();
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${queryString}`;
}

export function enrichHouseholdQualsWithResidentCompletedTabData(
  householdQualifications = [],
  residentAffordableQualifications = EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS,
) {
  if (!residentAffordableQualifications?.length) {
    return householdQualifications;
  }

  const residentById = new Map(
    residentAffordableQualifications
      .map((q) => {
        const id = getAffordableQualificationRowId(q);
        return id ? [id, q] : null;
      })
      .filter(Boolean),
  );

  return householdQualifications.map((householdQual) => {
    if (householdQual?.completedTabData) {
      return householdQual;
    }

    const qualId = getAffordableQualificationRowId(householdQual);
    if (!qualId) {
      return householdQual;
    }

    const residentQual = residentById.get(qualId);
    if (!residentQual?.completedTabData) {
      return householdQual;
    }

    return {
      ...householdQual,
      completedTabData: normalizeCompletedTabData(
        residentQual.completedTabData,
      ),
    };
  });
}

export function mapAffordableQualificationsForCertTab(
  affordableQualifications = [],
  tab,
) {
  if (tab !== CERT_TAB.COMPLETED) {
    return affordableQualifications;
  }

  return affordableQualifications.map((row) => ({
    ...qualRowForTab(row, CERT_TAB.COMPLETED),
    isCurrentlyReopened: isQualificationCurrentlyReopened(
      row?.qualificationData,
    ),
  }));
}

export function getHudCompletedTabParamsFromOptions(options = {}) {
  const {
    isHudCompletedTab = false,
    affordableQualificationId,
    ...fetchOptions
  } = options;

  return {
    isHudCompletedTab: Boolean(isHudCompletedTab),
    affordableQualificationId,
    fetchOptions,
  };
}

export function appendAffordableQualificationIdQuery(
  url,
  affordableQualificationId,
) {
  if (!affordableQualificationId) {
    return url;
  }

  const params = new URLSearchParams();
  params.set('affordableQualificationId', String(affordableQualificationId));
  const queryString = params.toString();
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${queryString}`;
}

export function buildGetUrlWithHudCompletedTab(
  baseUrl,
  { isHudCompletedTab = false, affordableQualificationId } = {},
) {
  if (isHudCompletedTab) {
    return appendIsHudCompletedTabQuery(baseUrl, {
      enabled: true,
      affordableQualificationId,
    });
  }

  return appendAffordableQualificationIdQuery(
    baseUrl,
    affordableQualificationId,
  );
}

export function hudCompletedTabQueryKeySegment(params = {}) {
  return { isHudCompletedTab: Boolean(params.isHudCompletedTab) };
}

export function mergeHudCompletedTabIntoRequestOptions(
  fetchOptions = {},
  params = {},
) {
  return {
    ...fetchOptions,
    isHudCompletedTab: Boolean(params.isHudCompletedTab),
    affordableQualificationId:
      params.affordableQualificationId ??
      fetchOptions.affordableQualificationId,
  };
}
